import {
  EntityLinks,
  Goal,
  GoalGroupBy,
  GoalLifeAreaLinkChange,
  GoalParentLinkChange,
  GoalReorderSectionUpdate,
  GroupByMode,
  ID,
  lifeAreaOtherID,
  rootId,
  TimelineCategory,
  TimelineDropSlot,
  TimelineDropTargetKinds,
  TimelineSubCategory,
  UpdateGoal,
} from '@griply/types';
import {
  getBlockedDropTargetIds,
  getDeadlineQuarterEndDate,
  getDeadlineQuarterKey,
  getDirectGoalLifeAreaIds,
  getGoalParentIds,
  NO_DEADLINE_QUARTER_KEY,
} from '@griply/utils';

export type ResolveTimelineDropOptions = {
  slot: TimelineDropSlot;
  draggedGoalId: ID;
  // The row that was picked up — a goal can render under several parents and life areas.
  sourceCategoryId: ID;
  sourceParentGoalId: ID | null;
  categories: TimelineCategory[];
  goals: Goal[];
  links?: EntityLinks;
  groupBy: GoalGroupBy;
};

export type TimelineDropUpdate = {
  updatedSections: GoalReorderSectionUpdate[];
  updatedGoals: UpdateGoal[];
  goalParentChanges: GoalParentLinkChange[];
  goalLifeAreaChanges: GoalLifeAreaLinkChange[];
};

const indexCategories = (
  categories: TimelineCategory[],
): Map<ID, TimelineSubCategory> => {
  const byGoalId = new Map<ID, TimelineSubCategory>();
  const walk = (nodes: TimelineSubCategory[]) =>
    nodes.forEach((node) => {
      byGoalId.set(node.goal.id, node);
      walk(node.subCategories);
    });
  categories.forEach((category) => walk(category.subCategories));
  return byGoalId;
};

const insertAt = (ids: ID[], id: ID, index: number): ID[] => [
  ...ids.slice(0, index),
  id,
  ...ids.slice(index),
];

export const resolveTimelineDrop = ({
  slot,
  draggedGoalId,
  sourceCategoryId,
  sourceParentGoalId,
  categories,
  goals,
  links,
  groupBy,
}: ResolveTimelineDropOptions): TimelineDropUpdate | null => {
  const dragged = goals.find(({ id }) => id === draggedGoalId);
  if (!dragged) {
    return null;
  }

  const targetIsGoal = slot.targetKind === TimelineDropTargetKinds.Goal;
  if (targetIsGoal) {
    const blockedIds = getBlockedDropTargetIds(draggedGoalId, goals, links);
    if (blockedIds.has(slot.targetId)) {
      return null;
    }
  }

  const tree = indexCategories(categories);
  if (!tree.has(draggedGoalId)) {
    return null;
  }

  const targetParentGoal = targetIsGoal
    ? goals.find(({ id }) => id === slot.targetId)
    : undefined;
  if (targetIsGoal && !targetParentGoal) {
    return null;
  }

  const categoryTopLevelIds = (categoryId: ID): ID[] =>
    categories
      .find(({ id }) => id === categoryId)
      ?.subCategories.map((subCategory) => subCategory.goal.id) ?? [];
  const goalChildIds = (goalId: ID): ID[] =>
    tree.get(goalId)?.subCategories.map((subCategory) => subCategory.goal.id) ??
    [];

  const targetChildren = targetIsGoal
    ? goalChildIds(slot.targetId)
    : categoryTopLevelIds(slot.categoryId);
  const withoutDragged = targetChildren.filter((id) => id !== draggedGoalId);
  const insertIndex = Math.min(slot.insertIndex, withoutDragged.length);
  const newTargetChildren = insertAt(
    withoutDragged,
    draggedGoalId,
    insertIndex,
  );

  const isLifeAreaGrouping = groupBy === GroupByMode.LifeArea;
  const isDeadlineGrouping = groupBy === GroupByMode.Deadline;

  const updatedGoals: UpdateGoal[] = [];
  const updatedSections: GoalReorderSectionUpdate[] = [];
  const goalParentChanges: GoalParentLinkChange[] = [];
  // The scalar alone would leave a migrated goal's relationship doc pointing at its old
  // life area, so the move is emitted as a link change the caller merges.
  const goalLifeAreaChanges: GoalLifeAreaLinkChange[] = [];

  // Only the picked-up link moves; the goal's other parents and life areas stay.
  const targetParentGoalId = targetIsGoal ? slot.targetId : null;
  // The drag started from a life area only when the goal sat there as a top-level row.
  const sourceLifeAreaId =
    isLifeAreaGrouping && !sourceParentGoalId ? sourceCategoryId : undefined;
  const remainingLifeAreaIds = getDirectGoalLifeAreaIds(dragged, links).filter(
    (id) => id !== sourceLifeAreaId,
  );
  if (targetParentGoalId !== sourceParentGoalId) {
    const remaining = getGoalParentIds(dragged, { links }).filter(
      (id) => id !== sourceParentGoalId,
    );
    goalParentChanges.push({
      goalId: draggedGoalId,
      parentIds: targetParentGoalId
        ? Array.from(new Set([...remaining, targetParentGoalId]))
        : remaining,
    });
  }

  const draggedUpdate: UpdateGoal = { id: draggedGoalId };
  const applyLifeAreaIds = (nextIds: ID[]) => {
    draggedUpdate.lifeAreaId = nextIds[0] ?? null;
    goalLifeAreaChanges.push({ goalId: draggedGoalId, lifeAreaIds: nextIds });
  };
  if (targetIsGoal) {
    draggedUpdate.ancestorIds = [
      ...(targetParentGoal!.ancestorIds ?? []),
      targetParentGoal!.id,
    ];
    applyLifeAreaIds(remainingLifeAreaIds);
  }
  if (!targetIsGoal) {
    draggedUpdate.ancestorIds = null;
    if (isLifeAreaGrouping) {
      applyLifeAreaIds(
        slot.categoryId === lifeAreaOtherID
          ? remainingLifeAreaIds
          : Array.from(new Set([...remainingLifeAreaIds, slot.categoryId])),
      );
    }
    if (
      isDeadlineGrouping &&
      slot.categoryId !== getDeadlineQuarterKey(dragged.deadline)
    ) {
      draggedUpdate.deadline =
        slot.categoryId === NO_DEADLINE_QUARTER_KEY
          ? null
          : getDeadlineQuarterEndDate(slot.categoryId);
    }
  }
  updatedGoals.push(draggedUpdate);

  if (targetIsGoal) {
    updatedGoals.push({ id: slot.targetId, goalSorting: newTargetChildren });
  }

  const leftSourceParent =
    !!sourceParentGoalId &&
    (!targetIsGoal || sourceParentGoalId !== slot.targetId);
  if (leftSourceParent) {
    updatedGoals.push({
      id: sourceParentGoalId,
      goalSorting: goalChildIds(sourceParentGoalId).filter(
        (id) => id !== draggedGoalId,
      ),
    });
  }

  if (isLifeAreaGrouping) {
    if (!targetIsGoal) {
      updatedSections.push({
        id: slot.categoryId,
        goalSorting: newTargetChildren,
      });
    }
    const leftSourceCategory =
      !sourceParentGoalId &&
      (targetIsGoal || sourceCategoryId !== slot.categoryId);
    if (leftSourceCategory) {
      updatedSections.push({
        id: sourceCategoryId,
        goalSorting: categoryTopLevelIds(sourceCategoryId).filter(
          (id) => id !== draggedGoalId,
        ),
      });
    }
    return {
      updatedSections,
      updatedGoals,
      goalParentChanges,
      goalLifeAreaChanges,
    };
  }

  // non-life-area groupings persist one flat root order across all categories
  const rootChanged = !targetIsGoal || !sourceParentGoalId;
  if (rootChanged) {
    const flatRootIds = categories.flatMap(({ id, subCategories }) => {
      const ids = subCategories
        .map((subCategory) => subCategory.goal.id)
        .filter((goalId) => goalId !== draggedGoalId);
      if (!targetIsGoal && id === slot.categoryId) {
        return insertAt(ids, draggedGoalId, insertIndex);
      }
      return ids;
    });
    updatedSections.push({ id: rootId, goalSorting: flatRootIds });
  }

  return {
    updatedSections,
    updatedGoals,
    goalParentChanges,
    goalLifeAreaChanges,
  };
};
